import type React from 'react'
import type { ApprovalItem, ApprovalItemAttachment } from '../../types/recurringTasks'
import { BlitzReportApprovalAction } from './BlitzReportApprovalAction'

/**
 * Props passed to a registered ApprovalAction component when the user clicks
 * "Approve" on a pending approval item in ApprovalsView.
 *
 * The component is responsible for performing whatever post-approval work is
 * required for its task type (e.g. parsing an attachment, opening a
 * validation modal, calling an upload endpoint) and then signalling success
 * via onComplete (which records the approval and refreshes the list) or
 * onCancel (which closes without persisting anything).
 */
export interface ApprovalActionProps {
  approval: ApprovalItem
  attachments: ApprovalItemAttachment[]
  onComplete: () => void
  onCancel: () => void
}

export interface RecurringTaskTypeDefinition {
  /** Matches RecurringSchedule.TaskType column. */
  value: string
  label: string
  description: string
  ApprovalAction: React.ComponentType<ApprovalActionProps>
}

/**
 * Registry of all known recurring task types. Add new types by editing this
 * file — there is no plugin loading or dynamic discovery.
 *
 * Used by:
 *   - RecurringTasksTab — to populate the task type dropdown
 *   - ApprovalsView — to dispatch the per-type approval action
 */
export const taskTypeRegistry: Record<string, RecurringTaskTypeDefinition> = {
  BlitzReport: {
    value: 'BlitzReport',
    label: 'Blitz Report',
    description: 'Telstra mobile blitz report uploaded against a customer profile',
    ApprovalAction: BlitzReportApprovalAction,
  },
}

export function getTaskType(value: string | undefined | null): RecurringTaskTypeDefinition | undefined {
  if (!value) return undefined
  return taskTypeRegistry[value]
}

/** Dropdown options for forms that pick a task type. */
export const TASK_TYPES = Object.values(taskTypeRegistry).map(({ value, label }) => ({ value, label }))
